import React, { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Select, Table } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { AicaEmailDigestFrequency, UserDataFragment } from 'types/graphql'

import { EllipsisButton } from 'src/components/EllipsisButton'
import { ActionItem } from 'src/govwell-ui/types'
import { formatUserName } from 'src/utils'

import { DIGEST_FREQUENCY_OPTIONS } from './digestConstants'

type SubscriptionData = {
  id: number
  uuid: string
  frequency: AicaEmailDigestFrequency
  createdAt: string
  updatedAt: string
  user: UserDataFragment
}

type Props = {
  subscriptions: SubscriptionData[]
  onUpdateFrequency: (id: number, frequency: AicaEmailDigestFrequency) => Promise<void>
  onRemove: (id: number) => Promise<void>
  isProcessing: boolean
}

const getOptionKey = (option: SelectOption<AicaEmailDigestFrequency>) => option.value

const EmailInsightsTable = ({
  subscriptions,
  onUpdateFrequency,
  onRemove,
  isProcessing,
}: Props) => {
  const getRowId = useCallback((sub: SubscriptionData) => `${sub.id}`, [])
  const tableProps = useTableState<SubscriptionData>({
    initialState: {
      sorting: [{ id: 'name', desc: false }],
    },
  })

  const handleFrequencyChange = useCallback(
    async (id: number, option: SelectOption<AicaEmailDigestFrequency> | undefined) => {
      if (option) {
        await onUpdateFrequency(id, option.value)
      }
    },
    [onUpdateFrequency]
  )

  const handleRemove = useCallback(
    async (id: number) => {
      await onRemove(id)
    },
    [onRemove]
  )

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<SubscriptionData>()

    return [
      columnHelper.accessor((sub) => formatUserName(sub.user), {
        id: 'name',
        header: 'Name',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((sub) => sub.user.emailAddress, {
        id: 'email',
        header: 'Email',
        cell: (info) => info.getValue(),
        enableSorting: false,
      }),
      columnHelper.accessor('frequency', {
        header: 'Frequency',
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const currentFrequency = getValue()
          const selectedOption = DIGEST_FREQUENCY_OPTIONS.find((o) => o.value === currentFrequency)
          return (
            <Select
              options={DIGEST_FREQUENCY_OPTIONS}
              selectedOption={selectedOption}
              onSelectedOptionChange={(option) => handleFrequencyChange(row.original.id, option)}
              getOptionKey={getOptionKey}
              isDisabled={isProcessing}
              ariaLabel="Subscription frequency"
            />
          )
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        meta: { textAlign: 'right' },
        cell: (info) => {
          const subscription = info.row.original
          const removeItem: ActionItem = {
            label: 'Remove subscriber',
            key: `${subscription.id}-remove`,
            icon: 'FaTrash',
            onClick: () => handleRemove(subscription.id),
            isDestructive: true,
          }
          return <EllipsisButton items={[removeItem]} />
        },
      }),
    ]
  }, [handleFrequencyChange, handleRemove, isProcessing])

  return (
    <Table
      columns={columns}
      data={subscriptions}
      enablePagination={false}
      getRowId={getRowId}
      isSortedOnClient
      totalCount={subscriptions.length}
      emptyStateMessage="No subscribers yet. Add team members to receive automated email digests."
      {...tableProps}
    />
  )
}

export default React.memo(EmailInsightsTable)
